import { QUERY_TABLES } from '@/helpers/constants';
import { executeQuery } from '@/helpers/request';
import { getRandomNumber900000to999999 } from '@/helpers/utility';

/**
 * Block 6: Fetch meeting_room_mart_reserved data
 * For reservation and usage time charts
 */
const buildQueryMeetingRoomMartReserved = ({ tenantId, branchId, months }) => {
  const randomId = getRandomNumber900000to999999();
  return {
    data: {
      fields: {
        device_identifier: ['select'],
        month: ['select'],
        tenant_id: ['select'],
        is_branch_total: ['select'],
        reserved_minutes: ['select'],
        reserved_rate: ['select'],
        used_minutes: ['select'],
        used_rate: ['select'],
        branch_id: ['select'],
        room_name: ['select'],
      },
      skip: 0,
      limit: 0,
    },
    filter: {
      tenant_id: [tenantId, randomId],
      ...(branchId && { branch_id: [branchId, randomId] }),
      ...(months && months.length > 0 && { month: { in: months } }),
    },
    scale: {},
    evol: {},
    total: {
      all: [],
    },
    order: {
      month: 'desc',
      room_name: 'asc',
    },
    table_name: QUERY_TABLES.MEETING_ROOM_MART_RESERVED,
  };
};

export const fetchMeetingRoomMartReserved = async ({ tenantId, branchId, months }) => {
  const request = buildQueryMeetingRoomMartReserved({ tenantId, branchId, months });
  return executeQuery(request);
};

/**
 * Block 7: Fetch meeting_room_mart_unused_cancelled data
 * For unused and released time charts
 */
const buildQueryMeetingRoomMartUnusedCancelled = ({ tenantId, branchId, months }) => {
  const randomId = getRandomNumber900000to999999();
  return {
    data: {
      fields: {
        tenant_id: ['select'],
        branch_id: ['select'],
        month: ['select'],
        unused_minutes: ['select'],
        cancelled_minutes: ['select'],
      },
      skip: 0,
      limit: 0,
    },
    filter: {
      tenant_id: [tenantId, randomId],
      ...(branchId && { branch_id: [branchId, randomId] }),
      ...(months && months.length > 0 && { month: { in: months } }),
    },
    scale: {},
    evol: {},
    total: {
      all: [],
    },
    order: {
      month: 'desc',
    },
    table_name: QUERY_TABLES.MEETING_ROOM_MART_UNUSED_CANCELLED,
  };
};

export const fetchMeetingRoomMartUnusedCancelled = async ({ tenantId, branchId, months }) => {
  const request = buildQueryMeetingRoomMartUnusedCancelled({ tenantId, branchId, months });
  return executeQuery(request);
};

/**
 * Block 8: Fetch meeting_room_mart_unused_organizers data
 * For organizers with unused reservations chart
 */
const buildQueryMeetingRoomMartUnusedOrganizers = ({ tenantId, branchId, months }) => {
  const randomId = getRandomNumber900000to999999();
  return {
    data: {
      fields: {
        tenant_id: ['select'],
        branch_id: ['select'],
        month: ['select'],
        organizer: ['select'],
        unused_minutes: ['select'],
        rank: ['select'],
      },
      skip: 0,
      limit: 0,
    },
    filter: {
      tenant_id: [tenantId, randomId],
      ...(branchId && { branch_id: [branchId, randomId] }),
      ...(months && months.length > 0 && { month: { in: months } }),
    },
    scale: {},
    evol: {},
    total: {
      all: [],
    },
    order: {
      month: 'desc',
      rank: 'asc',
    },
    table_name: QUERY_TABLES.MEETING_ROOM_MART_UNUSED_ORGANIZERS,
  };
};

export const fetchMeetingRoomMartUnusedOrganizers = async ({ tenantId, branchId, months }) => {
  const request = buildQueryMeetingRoomMartUnusedOrganizers({ tenantId, branchId, months });
  return executeQuery(request);
};

/**
 * Block 9: Fetch meeting_room_mart_people_rates data
 * For people rate distribution charts
 */
const buildQueryMeetingRoomMartPeopleRates = ({ tenantId, branchId, months }) => {
  const randomId = getRandomNumber900000to999999();
  return {
    data: {
      fields: {
        tenant_id: ['select'],
        branch_id: ['select'],
        device_identifier: ['select'],
        room_name: ['select'],
        month: ['select'],
        rate_1: ['select'],
        rate_2: ['select'],
        rate_3: ['select'],
        rate_4: ['select'],
        rate_5: ['select'],
      },
      skip: 0,
      limit: 0,
    },
    filter: {
      tenant_id: [tenantId, randomId],
      ...(branchId && { branch_id: [branchId, randomId] }),
      ...(months && months.length > 0 && { month: { in: months } }),
    },
    scale: {},
    evol: {},
    total: {
      all: [],
    },
    order: {
      month: 'desc',
      room_name: 'asc',
    },
    table_name: QUERY_TABLES.MEETING_ROOM_MART_PEOPLE_RATES,
  };
};

export const fetchMeetingRoomMartPeopleRates = async ({ tenantId, branchId, months }) => {
  const request = buildQueryMeetingRoomMartPeopleRates({ tenantId, branchId, months });
  return executeQuery(request);
};

/**
 * Block 10: Fetch meeting_room_mart_people_counts data
 * For people count pie chart
 */
const buildQueryMeetingRoomMartPeopleCounts = ({ tenantId, branchId, deviceIdentifier, months }) => {
  const randomId = getRandomNumber900000to999999();
  return {
    data: {
      fields: {
        device_identifier: ['select'],
        month: ['select'],
        count_category: ['select'],
        tenant_id: ['select'],
        room_name: ['select'],
        rate: ['select'],
        branch_id: ['select'],
      },
      skip: 0,
      limit: 0,
    },
    filter: {
      tenant_id: [tenantId, randomId],
      ...(branchId && { branch_id: [branchId, randomId] }),
      ...(deviceIdentifier && { device_identifier: [deviceIdentifier] }),
      ...(months && months.length > 0 && { month: { in: months } }),
    },
    scale: {},
    evol: {},
    total: {
      all: [],
    },
    order: {
      month: 'desc',
      count_category: 'asc',
    },
    table_name: QUERY_TABLES.MEETING_ROOM_MART_PEOPLE_COUNTS,
  };
};

export const fetchMeetingRoomMartPeopleCounts = async ({ tenantId, branchId, deviceIdentifier, months }) => {
  const request = buildQueryMeetingRoomMartPeopleCounts({ tenantId, branchId, deviceIdentifier, months });
  return executeQuery(request);
};

/**
 * Helper: Get last N months as date strings (YYYY-MM-01 format)
 * Uses manual formatting to avoid timezone issues with toISOString()
 */
export const getLastNMonths = (n = 4) => {
  const months = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}-01`);
  }
  return months;
};

/**
 * Helper: Format month for display (YYYY年MM月)
 */
export const formatMonthDisplay = (monthStr) => {
  if (!monthStr) return '';
  const date = new Date(monthStr);
  return `${date.getFullYear()}年${(date.getMonth() + 1).toString().padStart(2, '0')}月`;
};

/**
 * Helper: Format month short (MM月)
 */
export const formatMonthShort = (monthStr) => {
  if (!monthStr) return '';
  const date = new Date(monthStr);
  return `${date.getMonth() + 1}月`;
};

